import { KEY } from '@constants/key';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useLoginMutation } from '@store/api/authEndpoints';
import { gray } from '@theme/primitives';
import React, { useEffect, useRef, useState, type ChangeEvent } from 'react';

const SignInCard = () => {
  const [login, { isLoading }] = useLoginMutation();

  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isRememberMeChecked, setRememberMeChecked] = useState(false);
  const [usernameFieldValue, setUsernameFieldValue] = useState('');
  const passwordFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const username = localStorage.getItem(KEY.USERNAME);

    setRememberMeChecked(!!username);

    if (username) {
      setUsernameFieldValue(username);
      passwordFieldRef.current?.focus();
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const username = formData.get('username')?.toString() ?? '';
    const password = formData.get('password')?.toString() ?? '';

    if (!validate(username, password)) return;

    try {
      await login({ username, password }).unwrap();

      if (isRememberMeChecked) localStorage.setItem(KEY.USERNAME, username);
    } catch (_) {
      setPasswordError('Invalid username or password');
    }
  };

  const validate = (username: string, password: string): boolean => {
    setUsernameError(username.length < 3 ? 'Please enter a valid username.' : '');
    setPasswordError(password.length < 6 ? 'Password must be at least 6 characters long.' : '');

    return username.length >= 3 && password.length >= 6;
  };

  const handleRememberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRememberMeChecked(event.target.checked);

    if (!event.target.checked) localStorage.removeItem(KEY.USERNAME);
  };

  return (
    <Card variant='outlined'>
      <Typography component='h1' variant='h4' sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
        Sign in
      </Typography>

      <Box component='form' onSubmit={handleSubmit} noValidate sx={formBoxStyles}>
        <FormControl>
          <FormLabel htmlFor={KEY.USERNAME}>Username</FormLabel>
          <TextField
            defaultValue={usernameFieldValue}
            error={!!usernameError}
            helperText={usernameError}
            id={KEY.USERNAME}
            type='text'
            name='username'
            placeholder='admin'
            autoComplete='username'
            autoFocus
            required
            fullWidth
            variant='outlined'
            color={usernameError ? 'error' : 'primary'}
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor={KEY.PASSWORD}>Password</FormLabel>
          <TextField
            inputRef={passwordFieldRef}
            error={!!passwordError}
            helperText={passwordError}
            name='password'
            placeholder='••••••'
            type='password'
            id={KEY.PASSWORD}
            autoComplete='current-password'
            required
            fullWidth
            variant='outlined'
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>

        <FormControlLabel
          control={<Checkbox
            value='remember'
            color='primary'
            checked={isRememberMeChecked}
            onChange={handleRememberChange}
          />}
          label='Remember me'
        />

        <Button type='submit' fullWidth variant='contained' disabled={isLoading}>
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
      </Box>
    </Card>
  );
};

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  '&.MuiPaper-root': { background: '#05070A66 !important' },
  '& .MuiOutlinedInput-root': { background: `${gray[900]} !important` },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const formBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
};

export default SignInCard;
